import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import SkeletonLoader from '../skeleton-loader'
import {Button} from '../../core/shared/Button'
import {Tooltip} from '../../core/shared/Tooltip'
import {usePaymentCheckboxes} from './contexts/payment-checkboxes-context'
import {usePaymentAddress} from './contexts/payment-address-context'
import CheckIcon from '../../assets/check.svg'
import {PaymentForm} from './recurly-payment-form'
import {PaymentMethod} from './payment-method'
import {AchPaymentForm} from './ach-payment-form'
import {BecsPaymentForm} from './becs-payment-form'
import {InvoicePaymentForm} from './invoice-payment-form'
import {CCDesktopTodaySummary} from './cc-desktop-today-summary'
import {CCDesktopMonthlySummary} from './cc-desktop-monthly-summary'
import {useUserFeatures} from '../../context/user-features-context'
import {getTodayPreviewItems} from '../../utils/get-today-preview-items'
import {BillingNoticeModal} from '../../components/billing-notice-modal'
import thryvPeek from '../../assets/proposal-tool/thryv-peek-2.png'
import {PTArrowLeftSVG, PayNowArrowIcon} from '../../assets'
import achWarningIcon from '../../assets/ach-warning-icon.svg'
import keapLogo from '../../assets/proposal-tool/keap-white.png'
import logo from '../../assets/thryv-logo-white-letters.svg'
import '../../styles/formScreensStyles.css'
import {calculateCouponsAmountForItem} from '../../utils/coupon-helpers'
import {GST_MULTIPLIERS} from '../../utils/constants'
import {getSubscriptionDisclaimer} from '../../utils/payment-disclaimers'
import {ONE_TIME_FEES, KEEP_THRYV_PREFIX_PRODUCT_CODES} from './constants'
import {toast} from 'react-toastify'
import ExpiryCountdownTimer from '../expiry-countdown-timer'
import {formatPrice} from '../../utils/currency-format'
import {GradientTabs, GradientTab} from '../../core/shared/GradientTabs'
import {theme} from '../../pages/ProposalTool/features/Dashboard/theme'

export const BlueGradientBackground = ({
  showLogo = true,
  backgroundHexColor = 'linear-gradient(to bottom, #171d37, #6474a9)',
  width = '100%',
  className = '',
}) => {
  return (
    <section
      className={`blue-gradient-bg ${className}`}
      style={{
        background: backgroundHexColor,
        width,
      }}
    ></section>
  )
}

export const HeaderDivider = () => (
  <div
    style={{
      height: '36px',
      marginInline: '16px',
      flexShrink: 0,
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }}
  />
)

export function ThryvAndKeapHeader({
  linkExpiryInMilliSec,
  businessName,
  totalAmount,
}) {
  return (
    <header
      className="header-container"
      style={{
        paddingLeft: '32px',
        paddingRight: '24px',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          paddingBlock: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginRight: '22px',
        }}
      >
        <div
          style={{
            display: 'flex',
            marginRight: '120px',
            alignItems: 'center',
            gap: 0,
            flexShrink: 0,
          }}
        >
          <img src={logo} alt="Thryv Logo" className="header-logo" />
          <HeaderDivider />
          <img src={keapLogo} alt="Keap Logo" className="header-logo" />
        </div>
        {businessName && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                justifyContent: 'center',
              }}
            >
              <span className="payment-header-label">
                You&apos;re paying for
              </span>
              <span className="payment-header-value" style={{color: 'white'}}>
                {businessName}
              </span>
            </div>
          </>
        )}
        {linkExpiryInMilliSec > 0 && (
          <>
            <HeaderDivider />
            <ExpiryCountdownTimer
              countdownTimeMS={linkExpiryInMilliSec}
              isHourlyNeeded={linkExpiryInMilliSec > 60 * 60 * 1000}
            />
          </>
        )}
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <span
              style={{
                fontSize: '18px',
                lineHeight: '100%',
                height: '25px',
              }}
              className="payment-header-value"
            >
              Order Summary
            </span>
            <span
              className="payment-header-note"
              style={{maxWidth: '220px', height: '22px'}}
            >
              Future invoices will include any eligible discount for as long as
              your account qualifies.
            </span>
          </div>
        </div>
      </div>
      <div className="payment-total-container">
        <p className="payment-total-label">Total</p>
        <p className="payment-total-amount">
          {totalAmount?.replace(' + tax', '')}
          {totalAmount?.includes('+ tax') && (
            <span className="payment-total-label"> + tax</span>
          )}
        </p>
      </div>
    </header>
  )
}

const CCDesktop = ({
  isLoading,
  currentStep,
  paymentState,
  setPaymentState,
  handleChangePaymentMethod,
  paymentMethods,
  is5Order,
  calculateTaxes,
  calculatingTaxes,
  setCalculatingTaxes,
  setName,
  setIsAddressUpdated,
  isAddressUpdated,
  uuid,
  todayItems,
  totalNoTaxes,
  handleCompleteOrder,
  addressInformation,
  setAddressInformation,
  updateParentAddress,
  paymentRequestState,
  setPaymentRequestState,
  linkExpiryInMilliSec,
  isProcessPurchaseComplete,
  handleSuccess,
}) => {
  const navigate = useNavigate()
  const {checkBoxesValues, setCheckBoxesValues, isAgreeCheckboxDisabled} =
    usePaymentCheckboxes()
  const {isValidAddress, addressInformation: contextAddressInformation} =
    usePaymentAddress()
  const {Frontend_Preview_Payment: frontendPreviewPayment} = useUserFeatures()
  const [isBillingNoticeDisplayed, setIsBillingNoticeDisplayed] =
    React.useState(false)
  const [activeRightTab, setActiveRightTab] = useState('today')
  const [isPaymentMethodLoading, setIsPaymentMethodLoading] =
    React.useState(false)
  console.log('isBillingNoticeDisplayed :: ', isBillingNoticeDisplayed)
  const paymentPreview = paymentState.paymentInfo?.paymentPreview
  const locationPreviews = paymentState.paymentInfo?.locationPreviews
  const isAusNZ = ['AU', 'NZ'].includes(paymentState.paymentInfo?.country)
  const hasTaxComponent =
    paymentPreview?.tax != null ||
    Object.values(locationPreviews ?? {}).some(p => p.tax != null) ||
    paymentState.calculatedTaxes != null
  const isAnnualPlanIncluded =
    paymentState.annualAmount > 0 && paymentState.monthlyAmount === 0
  const summaryMonthlyAddons = isAnnualPlanIncluded
    ? paymentState.annualAddons
    : paymentState.monthlyAddons
  const summaryMonthlyLabel = isAnnualPlanIncluded
    ? 'Due Annually'
    : 'Due Monthly'

  const country = paymentState?.paymentInfo?.country

  function getTodayAmount() {
    let todayAmount
    // save-billing has run — its total (with address-adjusted tax) takes
    // priority over the initial payment-preview value.
    if (
      paymentState?.totalPaymentAmount &&
      paymentState.calculatedTaxes != null
    ) {
      todayAmount = paymentState.totalPaymentAmount
    } else if (frontendPreviewPayment && locationPreviews) {
      todayAmount = Object.values(locationPreviews).reduce(
        (sum, lp) => sum + (lp.total ?? 0),
        0,
      )
    } else if (
      !frontendPreviewPayment ||
      !paymentState.paymentInfo?.paymentPreview
    ) {
      todayAmount = paymentState?.totalPaymentAmount
        ? paymentState?.totalPaymentAmount
        : Math.max(totalNoTaxes - couponDiscountAmount, 0)
    } else {
      todayAmount = paymentState.paymentInfo.paymentPreview.total
    }
    return Math.max(
      todayAmount +
        (paymentState?.paymentInfo?.balanceInfo?.availableCreditAmount || 0),
      0,
    )
  }

  const hasAnnual = paymentState.annualAddons?.length > 0
  const hasMonthly =
    paymentState.monthlyAddons?.filter(i => i.currency.unitAmount > 0).length >
    0
  const isAnnual = hasAnnual && !hasMonthly
  const amount = isAnnual
    ? paymentState.annualAmount
    : paymentState.monthlyAmount

  const monthlyAlert =
    '*Unless noted as annual (12 months) on this page, the initial Subscription Period is 6 months. Please note, certain Thryv Services contain an additional per user fee which will vary based on the number of applicable users/employees/contractors'
  const annualAlert =
    '*Your Thryv Software Subscription Period is as specified on this page.  Any add-on Subscription Services have a minimum initial Subscription Period of 6 months.  Please note that certain Subscription Services contain an additional per-user fee, which will vary based on the number of applicable users/employees/contractors.'
  // FULSAS-14889: TGP plans use the shared disclaimer from payment-disclaimers.js
  const paymentNotificationAlert = getSubscriptionDisclaimer({
    items: todayItems,
    nonTgpDisclaimer: isAnnualPlanIncluded ? annualAlert : monthlyAlert,
  })
  const isAu = ['AU'].includes(paymentState?.paymentInfo?.country)
  const hasSupportFee =
    !frontendPreviewPayment || !paymentPreview
      ? todayItems.some(({code}) => code === '247support-m2m')
      : paymentPreview.apps.some(({planCode}) => planCode === '247support-m2m')
  const supportFee = todayItems.find(({code}) => code === '247support-m2m')
  const [couponDiscountAmount, setCouponDiscountAmount] = React.useState(0)
  const couponDiscountAmountRef = React.useRef(0)
  React.useEffect(() => {
    if (couponDiscountAmountRef.current !== couponDiscountAmount) {
      setCouponDiscountAmount(couponDiscountAmountRef.current)
    }
  })
  let listOfCouponsUsed = []

  const COMMAND_CENTER_NAME_MAPPING = {
    'cc_basic-5-thryv-m2m': 'My Account',
    'cc_basicpls-5-thryv-m2m': 'My Account',
    'cc_plus-5-thryv-m2m': 'My Account Plus',
    'cc_pro-5-thryv-m2m': 'My Account Pro',
  }

  const getProducts = (items, includeCoupons = false) => {
    const nestedAddonCodesBase = new Set(
      items.flatMap(item =>
        (item.addons || [])
          .filter(a => a.prodType === 'itemBasedAddon')
          .map(a => a.code),
      ),
    )

    if (!includeCoupons) {
      return items.filter(item => !nestedAddonCodesBase.has(item.code))
    }

    let itemsCouponDiscountAmount = 0

    // adding "Incentive" lines to the products list that has an applied coupons
    items = items.flatMap(current => {
      if (nestedAddonCodesBase.has(current.code)) return []
      if (current.code?.startsWith('cc_')) {
        let ccName = COMMAND_CENTER_NAME_MAPPING[current.code]
        current.name = ccName
      } else if (current.code?.startsWith('mc_')) {
        if (
          current.name.startsWith('Thryv') &&
          !KEEP_THRYV_PREFIX_PRODUCT_CODES.includes(current.code)
        ) {
          current.name = current.name.replace('Thryv', '')
        }
      } else if (
        current.code?.includes('addon-5-keap-addons') ||
        current.code?.includes('addon-5-keap-addons-annual') ||
        current.code?.startsWith('vert_') ||
        current.code?.startsWith('max')
      ) {
        const addOns = current?.addons
        if (
          current.name.startsWith('Thryv') &&
          !KEEP_THRYV_PREFIX_PRODUCT_CODES.includes(current.code)
        ) {
          current.name = current.name.replace('Thryv', '')
        }
        if (addOns && addOns !== undefined && addOns != null) {
          addOns.map(item => {
            if (item.name.startsWith('Thryv')) {
              return {
                ...item,
                name: item.name.replace('Thryv', ''),
              }
            }
            return item
          })
        }
      }
      const currentPrice = Number(current?.currency?.unitAmount || 0)
      const isCohort = paymentState?.paymentInfo?.isCohort

      // Items already on the account are "Included" today, so no coupon applies to them.
      if (!isCohort && currentPrice > 0 && !current.isExisting) {
        let price = 0
        let couponUsed = null

        // For multi-location orders use the per-location preview to read the
        // discount; fall back to the order-level paymentPreview for single-loc.
        const effectivePreview =
          locationPreviews?.[current.locationThryvId] ?? paymentPreview

        if (effectivePreview) {
          price = current.discount || 0
          if (price > 0 && paymentState?.paymentInfo?.coupons) {
            const calculatedAmount = calculateCouponsAmountForItem(
              current,
              paymentState.paymentInfo.coupons,
            )
            couponUsed = calculatedAmount.coupon
          }
        } else if (paymentState?.paymentInfo?.coupons) {
          const calculatedAmount = calculateCouponsAmountForItem(
            current,
            paymentState.paymentInfo.coupons,
          )
          couponUsed = calculatedAmount.coupon
          const currentCouponWaivedAmount = calculatedAmount.discountAmount

          price = currentCouponWaivedAmount
          itemsCouponDiscountAmount += currentCouponWaivedAmount
        }

        if (price > 0) {
          if (couponUsed) {
            listOfCouponsUsed.push({
              name: couponUsed?.name,
              description: couponUsed?.description,
              code: couponUsed.code,
              isCouponName: true,
              isCoupon: true,
              locationThryvId: current.locationThryvId,
            })
          }

          const isOneTimeFee = ONE_TIME_FEES.some(fee =>
            current?.name?.toLowerCase().includes(fee),
          )
          const gstMultiplier = isOneTimeFee
            ? GST_MULTIPLIERS[paymentState?.paymentInfo?.country] ?? 1
            : 1

          return [
            current,
            {
              name: couponUsed?.name || 'Incentive',
              currency: {
                unitAmount: price * gstMultiplier * -1,
              },
              isIncentive: true,
              isDiscount: couponUsed?.type === 'percent',
              appliedTo: current.code,
              sorting: current.name,
              // Synthesized client-side, so it has no order_line of its own
              // to carry a location_thryv_id — inherit the location of the
              // item it discounts, so it lands in that location's group
              // instead of falling back to the order's own thryvId (which,
              // on a multi-location Parent Pay order, matches neither real
              // location and would spawn a bogus extra location group).
              locationThryvId: current.locationThryvId,
            },
          ]
        }
      }
      return current
    })

    // removing duplicate coupons
    listOfCouponsUsed = listOfCouponsUsed.filter(
      (item, index, self) =>
        index === self.findIndex(t => t.code === item.code),
    )
    couponDiscountAmountRef.current = itemsCouponDiscountAmount
    return [...items, ...listOfCouponsUsed]
  }

  return (
    <>
      <BlueGradientBackground
        showLogo={false}
        backgroundHexColor="linear-gradient(to bottom, #171d37, #6474a9)"
      />
      <ThryvAndKeapHeader
        linkExpiryInMilliSec={linkExpiryInMilliSec}
        businessName={
          paymentState.paymentInfo?.businessAddress?.company ||
          paymentState.paymentInfo?.company
        }
        totalAmount={`${formatPrice(getTodayAmount(), 1, '', country)}${
          !isAusNZ && !hasTaxComponent ? ' + Tax' : ''
        }`}
      />
      {paymentState?.paymentInfo?.flow === 'upgrade' && (
        <div>
          <BillingNoticeModal
            setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
            calledFromPaymentLink={true}
          />
        </div>
      )}
      {isLoading ? (
        <section
          style={{
            padding: '40px 122px',
          }}
        >
          <SkeletonLoader width="100%" height="80vh" />
        </section>
      ) : (
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: '293fr 137fr',
            alignItems: 'stretch',
            width: '100%',
            gap: '26px',
            boxSizing: 'border-box',
            paddingInline: '24px',
          }}
        >
          <>
            {currentStep === 1 && (
              <>
                {/* SELECT PAYMENT METHOD */}
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    alignSelf: 'flex-start',
                    maxHeight: 'calc(100vh - 108px)',
                  }}
                >
                  <img
                    src={thryvPeek}
                    alt=""
                    style={{
                      position: 'absolute',
                      top: '0',
                      transform: 'translateY(-80%)',
                      right: '20px',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      overflow: 'hidden',
                      maxHeight: 'calc(100vh - 108px)',
                    }}
                  >
                    {/* PAYMENT METHOD HEADER */}
                    {(['US', 'AU'].includes(paymentState.paymentInfo.country) ||
                      isAusNZ) &&
                      (!paymentState?.paymentInfo?.isInvoiceBilled ||
                        isAusNZ) && (
                        <div
                          style={{
                            paddingInline: '20px',
                            paddingBlock: '12px',
                            flexShrink: 0,
                          }}
                        >
                          <section
                            className="d-flex"
                            style={{
                              alignItems: 'center',
                              gap: '20px',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                              }}
                            >
                              <div style={{flexShrink: 0}}>
                                <p className="select-payment-label">Select</p>
                                <p className="payment-method-label">
                                  Payment Method
                                </p>
                              </div>
                              <PaymentMethod
                                paymentMethod={paymentState.paymentMethod}
                                handleChangePaymentMethod={async method => {
                                  setIsPaymentMethodLoading(true)
                                  await handleChangePaymentMethod(method)
                                  setIsPaymentMethodLoading(false)
                                }}
                                paymentMethods={paymentMethods}
                                is5Order={is5Order}
                                isDesktop={true}
                                isLoading={isPaymentMethodLoading}
                              />
                            </div>
                            {/* this back button is only shown when the user navigates to this page 
                            from a previous page, not when they land directly on this page */}
                            {window.history.state?.idx > 0 && (
                              <Button
                                variant="secondary-gray"
                                type="button"
                                onClick={() => navigate(-1)}
                                leftIcon={
                                  <PTArrowLeftSVG width="14" height="14" />
                                }
                                size="md"
                                style={{
                                  marginRight: '10px',
                                }}
                              >
                                Back
                              </Button>
                            )}
                          </section>
                        </div>
                      )}

                    {paymentState?.paymentMethod?.value === 'ach' && (
                      <div
                        style={{
                          border: '1px solid #FBBD23',
                          backgroundColor: '#FEFCE8',
                          height: '30px',
                          gap: '10px',
                          borderBottomRightRadius: '5px',
                          borderBottomLeftRadius: '5px',
                          paddingTop: '5px',
                          paddingRight: '90px',
                          paddingBottom: '5px',
                          paddingLeft: '90px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxSizing: 'border-box',
                          fontFamily: 'Open Sans',
                          fontWeight: 700,
                          fontSize: '10px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          color: '#4D4D4D',
                        }}
                      >
                        <img
                          src={achWarningIcon}
                          alt=""
                          style={{
                            width: '19px',
                            height: '19px',
                          }}
                        />
                        Note: A monthly discount will be applied. Check your
                        invoice for details.
                      </div>
                    )}

                    {/* PAYMENT FORM */}
                    <div
                      className="payment-form-scroll"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '10px 10px 0px 0px',
                        padding: '20px 20px 20px 20px',
                        background: theme.colors.subtle,
                        overflowY: 'auto',
                      }}
                    >
                      {false && null}
                      {paymentState?.paymentInfo?.isInvoiceBilled &&
                        (!isAusNZ ||
                          paymentState?.paymentMethod?.value === 'invoice') && (
                          <>
                            <InvoicePaymentForm
                              calculateTaxes={calculateTaxes}
                              calculatingTaxes={calculatingTaxes}
                              setCalculatingTaxes={setCalculatingTaxes}
                              businessAddress={
                                paymentState.paymentInfo?.businessAddress
                              }
                              country={paymentState?.paymentInfo?.country}
                              setName={setName}
                              handleCompleteOrder={handleCompleteOrder}
                              setIsAddressUpdated={setIsAddressUpdated}
                              bizphone={
                                paymentState.paymentInfo?.businessAddress?.phone
                              }
                              is5Order={is5Order}
                              isAddressUpdated={isAddressUpdated}
                              uuid={uuid}
                              addressInformation={addressInformation}
                              setAddressInformation={setAddressInformation}
                              updateParentAddress={updateParentAddress}
                              disclaimerText={paymentNotificationAlert}
                            />
                          </>
                        )}
                      {paymentState?.paymentMethod?.value === 'card' &&
                        (!paymentState?.paymentInfo?.isInvoiceBilled ||
                          isAusNZ) && (
                          <PaymentForm
                            calculateTaxes={calculateTaxes}
                            calculatingTaxes={calculatingTaxes}
                            setCalculatingTaxes={setCalculatingTaxes}
                            businessAddress={
                              paymentState.paymentInfo?.businessAddress
                            }
                            country={paymentState?.paymentInfo?.country}
                            setName={setName}
                            handleCompleteOrder={handleCompleteOrder}
                            setIsAddressUpdated={setIsAddressUpdated}
                            bizphone={
                              paymentState.paymentInfo?.businessAddress?.phone
                            }
                            is5Order={is5Order}
                            isAddressUpdated={isAddressUpdated}
                            paymentPreview={paymentPreview}
                            paymentRequestState={paymentRequestState}
                            setPaymentRequestState={setPaymentRequestState}
                            disclaimerText={paymentNotificationAlert}
                          />
                        )}
                      {paymentState?.paymentMethod?.value === 'ach' &&
                        (!paymentState?.paymentInfo?.isInvoiceBilled ||
                          isAusNZ) && (
                          <AchPaymentForm
                            calculateTaxes={calculateTaxes}
                            calculatingTaxes={calculatingTaxes}
                            setCalculatingTaxes={setCalculatingTaxes}
                            businessAddress={
                              paymentState.paymentInfo?.businessAddress
                            }
                            country={paymentState?.paymentInfo?.country}
                            setName={setName}
                            handleCompleteOrder={handleCompleteOrder}
                            setIsAddressUpdated={setIsAddressUpdated}
                            bizphone={
                              paymentState.paymentInfo?.businessAddress?.phone
                            }
                            is5Order={is5Order}
                            isAddressUpdated={isAddressUpdated}
                            paymentPreview={paymentPreview}
                            disclaimerText={paymentNotificationAlert}
                          />
                        )}
                      {paymentState?.paymentMethod?.value === 'becs' && (
                        <BecsPaymentForm
                          calculateTaxes={calculateTaxes}
                          calculatingTaxes={calculatingTaxes}
                          setCalculatingTaxes={setCalculatingTaxes}
                          businessAddress={
                            paymentState.paymentInfo?.businessAddress
                          }
                          country={paymentState?.paymentInfo?.country}
                          setName={setName}
                          handleCompleteOrder={handleCompleteOrder}
                          setIsAddressUpdated={setIsAddressUpdated}
                          bizphone={
                            paymentState.paymentInfo?.businessAddress?.phone
                          }
                          is5Order={is5Order}
                          isAddressUpdated={isAddressUpdated}
                          paymentPreview={paymentPreview}
                          disclaimerText={paymentNotificationAlert}
                        />
                      )}
                      {!is5Order && paymentNotificationAlert && (
                        <p
                          className="card-number-helper-text"
                          style={{marginTop: '10px'}}
                        >
                          {paymentNotificationAlert}
                        </p>
                      )}
                    </div>

                    {/* PAYMENT FOOTER AND TERMS CHECKBOX */}
                    <div
                      style={{
                        paddingInline: '20px',
                        paddingTop: '10px',
                        paddingBottom: '14px',
                        backgroundColor: theme.colors.subtle,
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          boxShadow: '0 0 0 2px #5378FC',
                          background: '#ffffff',
                        }}
                      >
                        {isAgreeCheckboxDisabled ? (
                          <Tooltip
                            description="Please complete your payment information."
                            variant="top"
                          >
                            <span
                              onClick={
                                isAgreeCheckboxDisabled
                                  ? () =>
                                      toast.warn(
                                        'Please complete your payment information.',
                                      )
                                  : undefined
                              }
                              style={{
                                display: 'flex',
                                cursor: isAgreeCheckboxDisabled
                                  ? 'not-allowed'
                                  : 'auto',
                                flexShrink: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                id="terms"
                                name="terms"
                                checked={checkBoxesValues.agreedTerms}
                                disabled={isAgreeCheckboxDisabled}
                                onChange={a => {
                                  const value = a.currentTarget.checked
                                  setCheckBoxesValues({
                                    ...checkBoxesValues,
                                    agreedTerms: value,
                                  })
                                }}
                                style={{
                                  cursor: isAgreeCheckboxDisabled
                                    ? 'not-allowed'
                                    : 'pointer',
                                  width: '16px',
                                  height: '16px',
                                  accentColor: '#5378FC',
                                  pointerEvents: isAgreeCheckboxDisabled
                                    ? 'none'
                                    : 'auto',
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <span
                            style={{
                              display: 'flex',
                              cursor: 'auto',
                              flexShrink: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              id="terms"
                              name="terms"
                              checked={checkBoxesValues.agreedTerms}
                              disabled={false}
                              onChange={a => {
                                const value = a.currentTarget.checked
                                setCheckBoxesValues({
                                  ...checkBoxesValues,
                                  agreedTerms: value,
                                })
                              }}
                              style={{
                                cursor: 'pointer',
                                width: '16px',
                                height: '16px',
                                accentColor: '#5378FC',
                              }}
                            />
                          </span>
                        )}
                        <label
                          htmlFor="terms"
                          className="card-number-helper-text"
                          onClick={
                            isAgreeCheckboxDisabled
                              ? () =>
                                  toast.warn(
                                    'Please complete your payment information.',
                                  )
                              : undefined
                          }
                          style={{
                            marginTop: 0,
                            cursor: isAgreeCheckboxDisabled
                              ? 'not-allowed'
                              : 'pointer',
                            fontWeight: 700,
                            fontSize: '13px',
                          }}
                        >
                          By checking this box, you agree to our{' '}
                          <a
                            target="_blank"
                            href="https://corporate.thryv.com/terms/"
                            rel="noreferrer"
                            style={{fontWeight: 700, color: '#5378FC'}}
                          >
                            Terms & Conditions
                          </a>
                          <span style={{color: '#FF5000'}}> *</span>
                        </label>
                      </div>
                      {!is5Order && (
                        <p className="card-number-helper-text">
                          {
                            "By processing your payment, you agree that your personal information will be shared with Thryv's payment processor—Adyen, N.V. and will be subject to Adyen's "
                          }
                          <a
                            target="_blank"
                            href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                            rel="noreferrer"
                            style={{fontWeight: 700, color: '#5378FC'}}
                          >
                            privacy statement
                          </a>
                          {'.'}
                        </p>
                      )}
                    </div>
                  </div>
                </section>
                {/* ORDER SUMMARY */}
                <section
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    height: 'calc(100vh - 108px)',
                    maxHeight: 'calc(100vh - 108px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  {/* TABS HEADER */}
                  <div style={{flexShrink: 0, paddingInline: '10px'}}>
                    <GradientTabs
                      activeTab={activeRightTab}
                      onChange={setActiveRightTab}
                      variant="payment"
                    >
                      <GradientTab id="today">
                        <div className="label">
                          <p
                            className={`tab-label-title${
                              activeRightTab === 'today' ? ' active' : ''
                            }`}
                          >
                            Due Today{' '}
                            <span>
                              (
                              {
                                (todayItems ?? []).filter(
                                  i => i.code !== '247support-m2m',
                                ).length
                              }{' '}
                              Items)
                            </span>
                          </p>
                        </div>
                        <p
                          className={`tab-price${
                            activeRightTab === 'today' ? ' active' : ''
                          }`}
                        >
                          {formatPrice(getTodayAmount(), 1, '', country)}{' '}
                          {!isAusNZ && !hasTaxComponent && ' + Tax'}
                        </p>
                      </GradientTab>
                      <GradientTab id="monthly">
                        <div className="label blue">
                          <p
                            className={`tab-label-title${
                              activeRightTab === 'monthly' ? ' active' : ''
                            }`}
                          >
                            {summaryMonthlyLabel}{' '}
                            <span>
                              (
                              {
                                (summaryMonthlyAddons ?? []).filter(
                                  i => i.code !== '247support-m2m',
                                ).length
                              }{' '}
                              Items)
                            </span>
                          </p>
                        </div>
                        <p
                          className={`tab-price${
                            activeRightTab === 'monthly' ? ' active' : ''
                          }`}
                        >
                          {isAusNZ
                            ? formatPrice(amount, 1, '', country)
                            : `${formatPrice(amount, 1, '', country)} + Tax`}
                          {(!isAusNZ || isAu) && (
                            <span style={{color: '#FF5000'}}>*</span>
                          )}
                        </p>
                      </GradientTab>
                    </GradientTabs>
                  </div>

                  {/* SCROLLABLE PRODUCTS */}
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <div
                      className="payment-form-scroll"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        padding: '10px',
                        scrollbarGutter: 'stable',
                        background: '#DDE1EA',
                        borderRadius: '10px',
                        boxShadow: '0px 0px 22px 0px #00000040 inset',
                      }}
                    >
                      {activeRightTab === 'monthly' ? (
                        <CCDesktopMonthlySummary
                          getProducts={getProducts}
                          paymentState={paymentState}
                          isAusNZ={isAusNZ}
                          isAu={isAu}
                          hasSupportFee={supportFee}
                          supportFee={supportFee}
                          country={paymentState?.paymentInfo?.country}
                        />
                      ) : (
                        <CCDesktopTodaySummary
                          todayItems={
                            !frontendPreviewPayment || !paymentPreview
                              ? todayItems
                              : getTodayPreviewItems({
                                  paymentState,
                                  todayItems,
                                  is5Order,
                                })
                          }
                          getProducts={getProducts}
                          isAusNZ={isAusNZ}
                          hasSupportFee={hasSupportFee}
                          paymentState={paymentState}
                          supportFee={supportFee}
                          totalNoTaxes={totalNoTaxes}
                          couponDiscountAmount={couponDiscountAmount}
                          country={paymentState?.paymentInfo?.country}
                        />
                      )}
                    </div>
                  </div>

                  {/* BUTTON FOOTER */}
                  <div
                    style={{
                      padding: '10px',
                      borderTop: '1px solid #ECEEF1',
                      backgroundColor: 'white',
                      flexShrink: 0,
                    }}
                  >
                    {activeRightTab === 'monthly' && (
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#FF5000',
                          fontWeight: 700,
                          margin: '0 4px 4px 4px',
                        }}
                      >
                        {isAu
                          ? '*Excludes fees and incentives'
                          : '*Excludes taxes, fees, and incentives'}
                      </p>
                    )}
                    {paymentState.paymentInfo.isInvoiceBilled &&
                      (!isAusNZ ||
                        paymentState?.paymentMethod?.value === 'invoice') && (
                        <Button
                          variant="pay-now"
                          isActive={checkBoxesValues.agreedTerms}
                          type="button"
                          disabled={calculatingTaxes || !isValidAddress}
                          onClick={() => {
                            if (!checkBoxesValues.agreedTerms) {
                              toast.warn(
                                'Please agree to the Terms and Conditions to continue.',
                              )
                              return null
                            }
                            updateParentAddress(
                              contextAddressInformation,
                              paymentState.paymentInfo?.businessAddress?.phone,
                              handleCompleteOrder,
                            )
                          }}
                        >
                          Complete Purchase
                        </Button>
                      )}
                    {(!paymentState.paymentInfo.isInvoiceBilled ||
                      (isAusNZ &&
                        paymentState?.paymentMethod?.value !== 'invoice')) && (
                      <Button
                        variant="pay-now"
                        isActive={checkBoxesValues.agreedTerms}
                        type="button"
                        className="d-flex justify-content-around align-items-center"
                        onClick={() => {
                          if (!checkBoxesValues.agreedTerms) {
                            toast.warn(
                              'Please agree to the Terms and Conditions to continue.',
                            )
                            return null
                          }
                          if (
                            paymentState.paymentInfo.isInvoiceBilled &&
                            paymentState?.paymentMethod?.value === 'invoice'
                          ) {
                            updateParentAddress(
                              addressInformation,
                              paymentState.paymentInfo.businessAddress.phone,
                              handleCompleteOrder,
                            )
                          } else {
                            handleCompleteOrder()
                          }
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                          }}
                        >
                          <PayNowArrowIcon />
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <span className="pay-now-label">PAY NOW&nbsp;</span>
                            <span className="pay-now-amount">
                              {formatPrice(getTodayAmount(), 1, '', country)}
                              {!isAusNZ && !hasTaxComponent && ' + Tax'}
                            </span>
                          </span>
                        </div>
                      </Button>
                    )}
                  </div>
                </section>
              </>
            )}
            {currentStep === 2 &&
              !calculatingTaxes &&
              isProcessPurchaseComplete && (
                <section
                  className="acknowledgement-container"
                  style={{position: 'absolute'}}
                >
                  <div className="acknowledgement-card">
                    <div className="acknowledgement-content d-flex flex-column align-items-center">
                      <img src={CheckIcon} alt="Logo" className="check-icon" />
                      <p className="acknowledgement-title">Thank you</p>
                      <p
                        style={{
                          fontFamily: 'Open Sans',
                          fontWeight: '400',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '22px',
                          letterSpacing: '0px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          marginBottom: '16px',
                        }}
                      >
                        We will use this information to bill your Thryv
                        subscriptions each month. You can update your billing
                        information through the My Account page.
                      </p>
                      <p
                        style={{
                          fontFamily: 'Open Sans',
                          fontWeight: '700',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '22px',
                          letterSpacing: '0px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          color: '#4D4D4D',
                          marginBottom: '0px',
                        }}
                      >
                        Thank you for purchasing Thryv!
                      </p>
                    </div>
                  </div>
                </section>
              )}
          </>
        </section>
      )}
    </>
  )
}

export default CCDesktop
